import { useCallback } from 'react';
import noop from 'lodash/noop';

import { useTranslation } from '@lyearn/i18n';
import { EyeOffOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { InputBase, Stack, Typography } from '@lyearn/ui';
import { baseInputStyles } from '@/modules/corporate/modules/database/views/FormView/components/DatabaseFormEditor/components/styles';
import QuestionEntity from '@/modules/question/entity/Question';
import QuestionComment from '@/modules/user-survey/questions/components/QuestionComment';

import { QuestionEditorContainerProps } from './types';

const CLASSES = { multiline: 'p-0', input: 'p-0 h-auto' };

const QuestionEditorContainer: React.FC<QuestionEditorContainerProps> = (props) => {
  const {
    children,
    isSelected,
    question,
    onUpdateQuestion,
    isOptional,
    viewMode,
    userSurveyQuestion,
    onUpdateQuestionCommentTitle,
  } = props;

  const { t } = useTranslation();

  const onNameChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      onUpdateQuestion({ name: event.target.value, description: question.description ?? '' });
    },
    [onUpdateQuestion, question.description],
  );
  const onDescriptionChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      onUpdateQuestion({ description: event.target.value, name: question.questionText ?? '' });
    },
    [onUpdateQuestion, question.questionText],
  );

  // FIXME: inconsistencies in viewMode behavior. eg `viewMode !== 'view'` is also used in this file
  const isViewModeView = viewMode !== 'create';

  return (
    <Stack className="flex-1" direction="col">
      {userSurveyQuestion?.settings?.isHidden ? (
        <Stack alignItems="center" className="mb-4 ms-4" direction="row" gapX={8}>
          <EyeOffOutline className="h-20 w-20" fillColorToken="icon-secondary" />
          <Typography color="text-text-secondary" variant="label-02">
            {t('UserSurvey:Hidden')}
          </Typography>
        </Stack>
      ) : null}
      <Stack className="mb-24 flex-1 ms-8 xs:mb-16" direction="col">
        <Stack className="flex-1" gapX={8}>
          <Typography className="w-full" variant="heading-xs">
            <InputBase
              multiline
              className={cn('w-full text-text-primary', baseInputStyles)}
              classes={CLASSES}
              placeholder={t('UserSurvey:WriteYourQuestion')}
              readOnly={!isSelected}
              value={QuestionEntity.questionTextLabel(question.questionText)}
              onChange={onNameChange}
            />
          </Typography>
          {isOptional ? (
            <Typography as="span" color="text-text-placeholder" variant="body-short-02">
              {t('UserSurvey:Optional')}
            </Typography>
          ) : null}
        </Stack>
        {question.settings?.enableDescription ? (
          <Typography className="mt-4" color="text-text-secondary" variant="body-short-01">
            <InputBase
              multiline
              className={cn('w-full text-text-secondary', baseInputStyles)}
              classes={CLASSES}
              placeholder={t('Common:Form.Description')}
              readOnly={!isSelected}
              value={question.description}
              onChange={onDescriptionChange}
            />
          </Typography>
        ) : null}
      </Stack>
      {children}
      {userSurveyQuestion?.settings?.allowComments ? (
        <QuestionComment
          disableComments
          useRichTextForComment
          className="mt-24"
          commentTitle={userSurveyQuestion.commentTitle}
          post={undefined}
          onCommentTitleChange={
            onUpdateQuestionCommentTitle && isSelected && viewMode !== 'view'
              ? (val) => onUpdateQuestionCommentTitle(userSurveyQuestion.id, val)
              : undefined
          }
          onPostChange={noop}
        />
      ) : null}
    </Stack>
  );
};

export default QuestionEditorContainer;
